// Review comments: a single comment row (resolve / reopen) and the per-unit
// thread with a composer. Comments belong to one version; approval needs all
// of them resolved.

import { useState } from "react";
import { addComment, errorText, resolveComment } from "../../services/talkTracks";
import type { TalkTrackComment } from "../../types/talkTrack";
import { formatDateTime } from "../analytics/format";

export function CommentItem({
  comment,
  onChange,
  onError,
  onFocusUnit,
}: {
  comment: TalkTrackComment;
  onChange: (comment: TalkTrackComment) => void;
  onError: (message: string) => void;
  // When set, the unit id renders as a link that scrolls to the unit.
  onFocusUnit?: (unitId: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function toggle() {
    setBusy(true);
    try {
      onChange(await resolveComment(comment.id, !comment.resolved));
    } catch (e) {
      onError(`Comment update failed: ${errorText(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`tt-comment${comment.resolved ? " resolved" : ""}`}>
      <div className="tt-comment-body">{comment.body}</div>
      <div className="tt-comment-meta">
        <span className="mono">{comment.authorUid}</span>
        <span>· {formatDateTime(comment.createdAt)}</span>
        {comment.unitId && onFocusUnit && (
          <button className="tt-link" onClick={() => onFocusUnit(comment.unitId as string)}>
            {comment.unitId}
          </button>
        )}
        <div className="spacer" />
        <button className="btn btn-sm btn-ghost" onClick={toggle} disabled={busy}>
          {comment.resolved ? "Reopen" : "Resolve"}
        </button>
      </div>
    </div>
  );
}

export function CommentThread({
  topicId,
  version,
  unitId,
  comments,
  onComment,
  onError,
}: {
  topicId: string;
  version: number;
  unitId: string | null;
  comments: TalkTrackComment[];
  onComment: (comment: TalkTrackComment) => void;
  onError: (message: string) => void;
}) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function post() {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      onComment(await addComment(topicId, version, text, unitId ?? undefined));
      setBody("");
    } catch (e) {
      onError(`Comment failed: ${errorText(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tt-thread">
      {comments.length === 0 && <div className="muted tt-small">No comments on v{version} yet.</div>}
      {comments.map((c) => (
        <CommentItem key={c.id} comment={c} onChange={onComment} onError={onError} />
      ))}
      <div className="tt-composer">
        <textarea
          rows={2}
          aria-label={unitId ? `Comment on ${unitId}` : "Comment on this version"}
          placeholder={`Comment on v${version}…`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <button className="btn btn-sm btn-primary" onClick={post} disabled={busy || !body.trim()}>
          {busy ? "Posting…" : "Post"}
        </button>
      </div>
    </div>
  );
}
